/**
 * `<TeamAuditTickerSection/>` — the lead-side **Recent activity** tail of the persistent right panel.
 *
 * Renders the 5 most-recent rows (or `limit`) with a "See full activity →" deep link to the
 * Activity tab of the Team page (`/teams/:teamId/team/activity`). In the lead's next-steps
 * panel it is the quiet three-row tail under the steps (#1921).
 *
 * **Reads the activity feed (#2926 D9).** The rows come from
 * `GET /api/teams/{teamId}/activity?limit=<n>` through `useTeamActivity`, the read of the
 * Activity tab. It lists changes only, in ready-to-read words, so the tail and the tab say the
 * same thing. The old source, the audit read, cuts its list to the newest 50 rows before any
 * filter: each observer read and each lead home load writes a read row there, so one visit
 * could fill all 50 and leave the tail empty.
 *
 * **Fresh after a write.** Writes the feed records (publish, solve, a roster edit) invalidate
 * `TEAM_AUDIT_ROOT`. The tail keeps one local entry under that root, with no fetch, and reads
 * the feed again each time that entry is invalidated.
 *
 * **Resolves teamId from the URL.** On `/teams/:teamId/*` routes the id comes from `useParams`;
 * on `/cycles/:cycleId/*` routes it comes from `useCycle(cycleId).data.team_id`, so the tail
 * follows the lead onto roster pages.
 *
 * **Self-hides for non-leads.** The feed answers 403 to a member. The section checks the team
 * role first and renders `null` when the viewer has no lead view, so no fetch fails.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, History } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { useTeam } from "../api/useTeam";
import { type TeamActivityRow, useTeamActivity } from "../api/useTeamActivity";
import { addDays, todayInZone } from "../floor/dates";
import { useAuth } from "../lib/AuthContext";
import { hasLeadView } from "../lib/teamAccess";
import { formatAbsolute, formatRelativeTime } from "../lib/teamAuditFormat";
import { teamAuditKey } from "../lib/teamAuditKey";
import { useResolvedTeamId } from "../lib/useResolvedTeamId";
import { dayWords } from "../routes/team/oversight/accessWords";
import { AmbientErrorNotice, isUnexpectedApiError } from "./AmbientErrorNotice";
import { Skeleton } from "./ui";

const TICKER_ROW_LIMIT = 5;

export interface TeamAuditTickerSectionProps {
  /** How many rows to show (default 5). The lead's next-steps panel keeps
   *  the ticker as a three-row tail under its steps (#1921). */
  limit?: number;
}

/** The row in one line: the verb, then the parts the API sends. */
function rowWords(row: TeamActivityRow): string {
  return `${row.verb} ${row.parts.map((p) => p.t).join("")}`.trim();
}

/** "3 minutes ago"; a member's own step has no time, only the day: "today", "Tue 20 Oct". */
function rowTime(row: TeamActivityRow, timezone: string | undefined): string {
  if (row.at) return formatRelativeTime(row.at);
  const today = todayInZone(timezone);
  if (row.day === today) return "today";
  if (row.day === addDays(today, -1)) return "yesterday";
  return dayWords(row.day);
}

export function TeamAuditTickerSection({
  limit = TICKER_ROW_LIMIT,
}: TeamAuditTickerSectionProps = {}) {
  const { accountId } = useAuth();
  const { teamId, resolving: resolvingTeam } = useResolvedTeamId();

  // Role check first: the feed answers 403 to a member.
  const teamQuery = useTeam(teamId);
  const isLead = hasLeadView(teamQuery.data?.role);

  const feed = useTeamActivity(teamId ?? "", null, { limit, enabled: isLead });

  // A local entry under TEAM_AUDIT_ROOT: no fetch, only a stamp that moves on each invalidation.
  const stamp = useQuery({
    queryKey: teamAuditKey(teamId ?? "", accountId),
    enabled: Boolean(teamId && accountId && isLead),
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: () => Date.now(),
  });
  const firstStamp = useRef<number | null>(null);
  const refetchFeed = feed.refetch;
  useEffect(() => {
    if (!stamp.dataUpdatedAt) return;
    if (firstStamp.current === null) {
      firstStamp.current = stamp.dataUpdatedAt;
      return;
    }
    if (stamp.dataUpdatedAt !== firstStamp.current) void refetchFeed();
  }, [stamp.dataUpdatedAt, refetchFeed]);

  const rows = (feed.data?.pages[0]?.items ?? []).slice(0, limit);
  const timezone = teamQuery.data?.timezone;

  // Hide entirely when there's no team context or the viewer isn't a
  // lead — an expected absence. An *unexpected* error (401/403/5xx),
  // though, must not be swallowed: this section only ever mounts in the
  // lead panel, so a 403 here means a broken session, not "you're not a
  // lead". Surface a calm notice instead of a missing section.
  if (!teamId && !resolvingTeam) return null;
  if (teamQuery.isError) {
    if (isUnexpectedApiError(teamQuery.error)) {
      return (
        <AmbientErrorNotice
          onRetry={() => void teamQuery.refetch()}
          label="Recent activity"
          testId="team-audit-ticker-section-error"
        />
      );
    }
    return null;
  }
  if (teamQuery.data && !isLead) return null;
  if (feed.isError) {
    if (isUnexpectedApiError(feed.error)) {
      return (
        <AmbientErrorNotice
          onRetry={() => void feed.refetch()}
          label="Recent activity"
          testId="team-audit-ticker-section-error"
        />
      );
    }
    return null;
  }

  const loading = resolvingTeam || teamQuery.isLoading || feed.isLoading;

  return (
    <section
      data-testid="team-audit-ticker-section"
      className="flex flex-col gap-2 px-1 py-2"
      aria-label="Recent team activity"
    >
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-fg">
          <History className="h-3.5 w-3.5 text-fg-subtle" aria-hidden="true" />
          Recent activity
        </div>
        {teamId ? (
          <Link
            to={`/teams/${teamId}/team/activity`}
            className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
            data-testid="team-audit-ticker-deep-link"
          >
            See full activity
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        ) : null}
      </header>

      {loading ? (
        <div className="flex flex-col gap-1.5" data-testid="team-audit-ticker-loading">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-fg-muted" data-testid="team-audit-ticker-empty">
          Nothing recorded yet for this team.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5" data-testid="team-audit-ticker-list">
          {rows.map((row) => (
            <li
              key={row.id}
              data-testid={`team-audit-ticker-row-${row.id}`}
              className="flex flex-col gap-0.5 rounded-sm bg-bg-elevated/60 p-2"
            >
              <p className="text-xs font-medium leading-tight text-fg">{rowWords(row)}</p>
              <span
                className="text-[10px] uppercase tracking-wide text-fg-subtle"
                title={row.at ? formatAbsolute(row.at) : undefined}
              >
                {rowTime(row, timezone)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
